/**
 * Guyana HPV-voucher submission layout ("HPV SERVICE PROVIDER DATA").
 *
 * The Ministry's HPV programme has its own workbook, separate from the general
 * voucher sheet in voucherColumns.js: columns A..W, in order. Same column
 * builders, so the page grids, copies and pastes it the same way.
 *
 * An order belongs on it when it carries a finalized HPV result: any test whose
 * code starts with HPV. Ultracare reports HPV16 / HPV18 / HPVOTHER, each
 * Negative/Positive (older results say Not Detected/Detected); Eureka reports a
 * single HPVHR whose value names the strain (HPV-16, HPV-18, Other High-Risk,
 * Not Detected). Both read the same way here.
 *
 * Left blank, as the LIS doesn't capture them: No. (the sheet's own running
 * number), middle name, Region 4 breakdown, past medical history, HPV
 * vaccination status, comment.
 */
import {
    blank, field, capFirst, voucherDate, formatDMY, computeAge, voucherSheet,
} from './voucherColumns';

const isHpv = (r) => /^HPV/i.test(r.testCode || '');

// The strains the sheet's "HPV Strains Identified" column names, in its order.
const STRAINS = [
    [/(^|\D)16(\D|$)/, 'HPV 16'],
    [/(^|\D)18(\D|$)/, 'HPV 18'],
    [/OTHER/i, 'Other high-risk HPV'],
];
const strainsIn = (text) => STRAINS.filter(([re]) => re.test(text)).map(([, label]) => label);

/**
 * One HPV result -> the strains it found: [] when negative, null when it can't
 * be read as either (invalid / inconclusive run).
 */
function strainsOf(r) {
    const v = String(r.value ?? '').trim().toUpperCase();
    if (!v) return null;
    if (/^(NEGATIVE|NOT[\s-]*DETECTED)$/.test(v)) return [];
    if (/^(POSITIVE|DETECTED)$/.test(v)) {
        // A per-strain test (HPV16, "Other High-Risk HPV"): its name says which.
        const named = strainsIn(`${r.testCode || ''} ${r.name || ''}`);
        return named.length ? named : [r.name || r.testCode];
    }
    const named = strainsIn(v); // a single test whose value names the strain
    return named.length ? named : null;
}

// Positive / Negative / Inconclusive (the sheet's list) + the strains found.
export function hpvOutcome(rec) {
    const results = (rec.results || []).filter(isHpv);
    const strains = [];
    let unreadable = false;
    results.forEach((r) => {
        const found = strainsOf(r);
        if (found === null) unreadable = true;
        else found.forEach((s) => { if (!strains.includes(s)) strains.push(s); });
    });
    const order = (s) => { const i = STRAINS.findIndex(([, label]) => label === s); return i < 0 ? STRAINS.length : i; };
    strains.sort((a, b) => order(a) - order(b));
    const result = strains.length ? 'Positive' : unreadable ? 'Inconclusive' : results.length ? 'Negative' : '';
    return { result, strains: strains.join(', ') };
}

// "Date Sample Processed" = when the HPV result was entered (the analyzer path
// doesn't stamp the order's resultedAt), else the order's resulted date.
const processedDate = (rec, ctx) => {
    const entered = (rec.results || []).filter(isHpv).map((r) => r.enteredAt).filter(Boolean).sort();
    return formatDMY(entered[entered.length - 1] || rec.resultedAt, ctx.tz);
};

// The app's ethnicity options -> the wording of the sheet's Ethnicity list.
const SHEET_ETHNICITY = {
    'guyanese of indian descent': 'Guyanese of Indian descent',
    'guyanese of african descent': 'Guyanese of African descent',
    'guyanese of amerindian descent': 'Guyanese Amerindian',
    'guyanese of mixed descent': 'Guyanese of Mixed Ancestry',
    'guyanese of chinese descent': 'Guyanese of Chinese descent',
    'guyanese of portuguese descent': 'Guyanese of Portuguese descent',
    'guyanese of european descent': 'Other',
    other: 'Other',
};
const sheetEthnicity = (value) => {
    const t = String(value || '').trim();
    return SHEET_ETHNICITY[t.toLowerCase()] || t;
};

// ---- the 23 columns, A..W, in order (headers as the Ministry sheet spells them)

export const HPV_VOUCHER_COLUMNS = [
    blank('No.'),
    field('Invoice No.', (rec) => rec.invoice?.invoiceNumber || ''),
    field('Service Provider Name', (rec, ctx) => ctx.providerName || ''),
    field('Date Voucher Used (dd/mm/yyyy)', voucherDate),
    field('Voucher Serial No.', (rec) => rec.invoice?.voucherSerialNo || ''),
    field('User First Name(s)', (rec) => rec.patient?.fName || ''),
    blank('User Middle Name(s)'),
    field('User Last Name(s)', (rec) => rec.patient?.lName || ''),
    field('User ID or Passport', (rec) => rec.patient?.nationalId || ''),
    field('User Sex', (rec) => capFirst(rec.patient?.gender)),
    field('User Age', (rec) => computeAge(rec.patient?.dob), undefined, { num: true }),
    field('User DOB (dd/mm/yyyy)', (rec) => formatDMY(rec.patient?.dob)),
    field('User Address', (rec) => rec.patient?.address || ''),
    field('User Region', (rec) => rec.patient?.region || ''),
    blank('Region 4 Breakdown'),
    field('User Telepone No.', (rec) => rec.patient?.contactNumber || ''),
    field('User Ethnicity', (rec) => sheetEthnicity(rec.patient?.ethnicity)),
    blank('User Past Medical History'),
    blank('User HPV Vaccination Status'),
    field('Date Sample Processed (dd/mm/yyyy)', processedDate),
    field('Results of HPV Test', (rec) => hpvOutcome(rec).result),
    field('HPV Strains Identified', (rec) => hpvOutcome(rec).strains),
    blank('Comment'),
];

export const HPV_VOUCHER_SHEET = voucherSheet({
    key: 'hpv',
    label: 'HPV',
    columns: HPV_VOUCHER_COLUMNS,
    includes: (rec) => (rec.results || []).some(isHpv),
    emptyText: 'No finalized HPV results in this period.',
    note: 'Orders with an HPV result, in the HPV voucher workbook’s columns. No., middle name, Region 4 breakdown, '
        + 'past medical history, vaccination status and comment aren’t recorded here — fill them in the workbook.',
});
